import React from "react"
import { ui } from "../../../shared/styles/uiSystem"
import SectionHeading from "../../../shared/components/SectionHeading"
import SubscriptionFeatureAccess from "../../subscriptions/components/SubscriptionFeatureAccess"
import {
  MetricChip,
  Tag,
  EmptyState,
  CollapseButton
} from "./DashboardAtoms"

export default function GlobalExecutiveSection({
  multiClinicExecutiveEnabled,
  multiClinicExecutiveFeatureKey,
  currentPlanName,
  openUpgrade,
  collapsedSections,
  toggleSection,
  filteredExecutiveScore,
  loading,
  getScoreBadge,
  executiveRows,
  isTablet,
  isMobile,
  highlights,
  insights,
  effectiveCanAdvancedReports,
  formatMoney,
  formatPct
}) {
  const scoreRows = Array.isArray(filteredExecutiveScore) ? filteredExecutiveScore : []
  const comparativeRows = Array.isArray(executiveRows) ? executiveRows : []
  const recommendations = Array.isArray(insights?.recommendations)
    ? insights.recommendations
    : []

  const summary = buildGlobalSummary(comparativeRows)
  const quickHighlights = buildGlobalHighlights({
    highlights,
    formatMoney,
    formatPct
  })

  const insightCards = buildGlobalInsightCards(insights)

  const shouldRenderInsightsSection =
    !effectiveCanAdvancedReports ||
    loading ||
    recommendations.length > 0 ||
    insightCards.length > 0

  return (
    <>
      <SubscriptionFeatureAccess
        enabled={multiClinicExecutiveEnabled}
        featureKey={multiClinicExecutiveFeatureKey}
        planName={currentPlanName}
        subtitle="Compara el desempeño de varias clínicas desde un solo panel."
        onUpgrade={() => openUpgrade(multiClinicExecutiveFeatureKey)}
      >
        <div style={{ ...ui.card, marginBottom: 16, padding: 14 }}>
          <SectionHeading
            title="Semáforo ejecutivo por clínica"
            subtitle="Salud del negocio por sede"
            actions={
              <CollapseButton
                collapsed={!!collapsedSections.executiveScore}
                onClick={() => toggleSection("executiveScore")}
              />
            }
          />

          {!collapsedSections.executiveScore && (
            <>
              {!scoreRows.length ? (
                <EmptyState loading={loading} text="Sin score disponible." />
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      isTablet || isMobile
                        ? "1fr"
                        : "repeat(2, minmax(0, 1fr))",
                    gap: 12,
                    alignItems: "start"
                  }}
                >
                  {scoreRows.map((row) => (
                    <ExecutiveScoreClinicCard
                      key={row.clinicId}
                      row={row}
                      getScoreBadge={getScoreBadge}
                      formatMoney={formatMoney}
                      formatPct={formatPct}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </SubscriptionFeatureAccess>

      <SubscriptionFeatureAccess
        enabled={multiClinicExecutiveEnabled}
        featureKey={multiClinicExecutiveFeatureKey}
        planName={currentPlanName}
        subtitle="Compara sedes, producción y ocupación desde una vista consolidada."
        onUpgrade={() => openUpgrade(multiClinicExecutiveFeatureKey)}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              isTablet || isMobile
                ? "1fr"
                : "minmax(0, 1.25fr) minmax(300px, 0.75fr)",
            gap: 16,
            marginBottom: 16,
            alignItems: "start"
          }}
        >
          <div style={{ ...ui.card, padding: 14 }}>
            <SectionHeading
              title="Comparativo por clínica"
              subtitle="Producción, cobranza y ocupación por sede"
              actions={
                <CollapseButton
                  collapsed={!!collapsedSections.comparative}
                  onClick={() => toggleSection("comparative")}
                />
              }
            />

            {!collapsedSections.comparative && (
              <>
                {!comparativeRows.length ? (
                  <EmptyState loading={loading} text="Sin información ejecutiva." />
                ) : (
                  <div style={{ display: "grid", gap: 10 }}>
                    {comparativeRows.map((row) => (
                      <ComparativeClinicCard
                        key={row.clinicId}
                        row={row}
                        formatMoney={formatMoney}
                        formatPct={formatPct}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div
            style={{
              ...ui.card,
              padding: 14,
              display: "grid",
              gap: 12,
              alignContent: "start"
            }}
          >
            <SectionHeading
              title="Resumen consolidado"
              subtitle="Lo importante sin meter ruido"
            />

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 10
              }}
            >
              <MetricChip
                label="Clínicas visibles"
                value={formatCount(summary.clinics)}
              />
              <MetricChip
                label="Doctores"
                value={formatCount(summary.doctors)}
              />
              <MetricChip
                label="Citas"
                value={formatCount(summary.appointments)}
              />
              <MetricChip
                label="Pendiente total"
                value={`$${formatMoney(summary.pending)}`}
              />
            </div>

            {quickHighlights.length ? (
              <div style={{ display: "grid", gap: 8 }}>
                {quickHighlights.map((item, index) => (
                  <CompactHighlightCard
                    key={`${item.label}-${index}`}
                    label={item.label}
                    value={item.value}
                  />
                ))}
              </div>
            ) : (
              <EmptyState loading={loading} text="Sin claves relevantes para esta fecha." />
            )}
          </div>
        </div>
      </SubscriptionFeatureAccess>

      {shouldRenderInsightsSection && (
        <SubscriptionFeatureAccess
          enabled={effectiveCanAdvancedReports}
          featureKey="advancedReports"
          planName={currentPlanName}
          subtitle="Desbloquea interpretaciones, hallazgos y lectura de negocio avanzada."
          onUpgrade={() => openUpgrade("advancedReports")}
        >
          <div style={{ ...ui.card, marginBottom: 16, padding: 14 }}>
            <SectionHeading
              title="Lecturas ejecutivas"
              subtitle="Interpretación del negocio"
              actions={
                <CollapseButton
                  collapsed={!!collapsedSections.insights}
                  onClick={() => toggleSection("insights")}
                />
              }
            />

            {!collapsedSections.insights && (
              <>
                {!!insightCards.length && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: 10,
                      marginBottom: recommendations.length ? 12 : 0
                    }}
                  >
                    {insightCards.map((item, index) => (
                      <CompactHighlightCard
                        key={`${item.label}-${index}`}
                        label={item.label}
                        value={item.value}
                      />
                    ))}
                  </div>
                )}

                {recommendations.length ? (
                  <div style={{ display: "grid", gap: 10 }}>
                    {recommendations.map((text, index) => (
                      <ExecutiveReadingCard
                        key={index}
                        index={index}
                        text={text}
                      />
                    ))}
                  </div>
                ) : !insightCards.length ? (
                  <EmptyState loading={loading} text="Sin lecturas disponibles para esta fecha." />
                ) : null}
              </>
            )}
          </div>
        </SubscriptionFeatureAccess>
      )}
    </>
  )
}

function ExecutiveScoreClinicCard({
  row,
  getScoreBadge,
  formatMoney,
  formatPct
}) {
  const badge = getScoreBadge(row.status)

  return (
    <div
      style={{
        padding: 14,
        borderRadius: 16,
        border: `1px solid ${badge.border}`,
        background: "#ffffff",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)"
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: 10
        }}
      >
        <div style={{ fontWeight: 900, fontSize: 16, color: "#0f172a" }}>
          {row.clinicName}
        </div>

        <div
          style={{
            padding: "6px 10px",
            borderRadius: 999,
            background: badge.background,
            color: badge.color,
            border: `1px solid ${badge.border}`,
            fontWeight: 900,
            fontSize: 12
          }}
        >
          {badge.label} · Score {row.score}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
          gap: 10,
          marginBottom: row.reasons?.length ? 10 : 0
        }}
      >
        <MetricChip label="Producción" value={`$${formatMoney(row.production)}`} />
        <MetricChip label="Cobrado" value={`$${formatMoney(row.collected)}`} />
        <MetricChip label="Pendiente" value={`$${formatMoney(row.pending)}`} />
        <MetricChip label="Ocupación" value={`${formatPct(row.occupationPct)}%`} />
      </div>

      {!!row.reasons?.length && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {row.reasons.map((reason, index) => (
            <Tag key={index}>{reason}</Tag>
          ))}
        </div>
      )}
    </div>
  )
}

function ComparativeClinicCard({ row, formatMoney, formatPct }) {
  return (
    <div
      style={{
        borderRadius: 16,
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        padding: 14,
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)"
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: 10
        }}
      >
        <div style={{ display: "grid", gap: 4 }}>
          <div style={{ fontWeight: 900, fontSize: 16, color: "#0f172a" }}>
            {row.clinicName}
          </div>

          <div
            style={{
              color: "#64748b",
              fontSize: 12.5,
              fontWeight: 700
            }}
          >
            {formatCount(row.doctors)} doctores · {formatCount(row.appointments)} citas
          </div>
        </div>

        <div
          style={{
            padding: "6px 10px",
            borderRadius: 999,
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            color: "#0f172a",
            fontSize: 12,
            fontWeight: 900
          }}
        >
          Ocupación {formatPct(row.occupationPct)}%
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
          gap: 10
        }}
      >
        <MetricChip label="Producción" value={`$${formatMoney(row.production)}`} />
        <MetricChip label="Cobrado" value={`$${formatMoney(row.collected)}`} />
        <MetricChip label="Pendiente" value={`$${formatMoney(row.pending)}`} />
      </div>
    </div>
  )
}

function CompactHighlightCard({ label, value }) {
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 14,
        background: "#f8fafc",
        border: "1px solid #e5e7eb",
        display: "grid",
        gap: 4
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 800,
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: 0.3
        }}
      >
        {label}
      </div>

      <div
        style={{
          color: "#0f172a",
          fontSize: 14,
          fontWeight: 900,
          lineHeight: 1.3
        }}
      >
        {value}
      </div>
    </div>
  )
}

function ExecutiveReadingCard({ index, text }) {
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 14,
        background: "#f8fafc",
        border: "1px solid #e5e7eb",
        display: "grid",
        gap: 6
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 800,
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: 0.3
        }}
      >
        Lectura {index + 1}
      </div>

      <div
        style={{
          color: "#334155",
          fontSize: 13,
          lineHeight: 1.45
        }}
      >
        {text}
      </div>
    </div>
  )
}

function buildGlobalSummary(rows) {
  return rows.reduce(
    (acc, row) => {
      acc.clinics += 1
      acc.doctors += toNumber(row.doctors)
      acc.appointments += toNumber(row.appointments)
      acc.pending += toNumber(row.pending)
      return acc
    },
    {
      clinics: 0,
      doctors: 0,
      appointments: 0,
      pending: 0
    }
  )
}

function buildGlobalHighlights({ highlights, formatMoney, formatPct }) {
  const items = []

  if (highlights?.topClinic) {
    items.push({
      label: "Clínica más productiva",
      value: `${highlights.topClinic.clinicName} · $${formatMoney(highlights.topClinic.production)}`
    })
  }

  if (highlights?.highestPendingClinic) {
    items.push({
      label: "Mayor cartera pendiente",
      value: `${highlights.highestPendingClinic.clinicName} · $${formatMoney(highlights.highestPendingClinic.pending)}`
    })
  }

  if (highlights?.lowestOccupationClinic) {
    items.push({
      label: "Menor ocupación",
      value: `${highlights.lowestOccupationClinic.clinicName} · ${formatPct(highlights.lowestOccupationClinic.occupationPct)}%`
    })
  }

  items.push({
    label: "Modo actual",
    value: "Consolidado multi-clínica"
  })

  return items
}

function buildGlobalInsightCards(insights) {
  const cards = []

  if (insights?.bestProductionClinic) {
    cards.push({
      label: "Sede más productiva",
      value: insights.bestProductionClinic.clinicName
    })
  }

  if (insights?.highestOccupationClinic) {
    cards.push({
      label: "Sede más ocupada",
      value: insights.highestOccupationClinic.clinicName
    })
  }

  if (insights?.lowestOccupationClinic) {
    cards.push({
      label: "Sede menos ocupada",
      value: insights.lowestOccupationClinic.clinicName
    })
  }

  return cards
}

function toNumber(value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function formatCount(value) {
  return new Intl.NumberFormat("es-MX", {
    maximumFractionDigits: 0
  }).format(toNumber(value))
}